/**
 * channel-access-controller.ts
 *
 * Deny-First Access Resolution Controller:
 * Precedence: DenyOverride > AllowOverride > RoleMatch > GroupMatch > PublicAccess > DefaultDeny
 * Emits 'security.channel_access_denied' audit events on OrchestrationEventBus when denied.
 */

import {
  AgentChannelDefinition,
  ChannelAccessVerdict,
  ChannelAccessRole,
  ActionPermission,
  ChannelAccessDeniedError,
} from '../channels/agent-channel-contracts.js';
import type { OrchestrationStore } from '../../mesh/orchestration/storage/orchestration-store.js';

export interface UserAccessContext {
  readonly principalId: string;
  readonly role?: ChannelAccessRole;
  readonly userGroupIds?: readonly string[];
  readonly isAuthenticated: boolean;
}

export class ChannelAccessController {
  constructor(private readonly store?: OrchestrationStore) {}

  /**
   * Evaluates channel access using strict deny-first precedence.
   */
  evaluateChannelAccess(
    channel: AgentChannelDefinition,
    user: UserAccessContext
  ): ChannelAccessVerdict {
    const now = new Date().toISOString();
    const policy = channel.accessPolicy;

    // Disabled channel check
    if (!channel.enabled) {
      return {
        allowed: false,
        channelId: channel.id,
        principalId: user.principalId,
        matchedRule: 'default_deny',
        reason: 'Channel is currently disabled',
        evaluatedAt: now,
      };
    }

    // Step 1: Explicit Deny (Overrides everything)
    if (policy.deniedPrincipalIds && policy.deniedPrincipalIds.includes(user.principalId)) {
      return {
        allowed: false,
        channelId: channel.id,
        principalId: user.principalId,
        matchedRule: 'explicit_deny',
        reason: `Principal ${user.principalId} is explicitly denied access to channel ${channel.id}`,
        evaluatedAt: now,
      };
    }

    // Step 2: Explicit Allow
    if (policy.allowedPrincipalIds && policy.allowedPrincipalIds.includes(user.principalId)) {
      return {
        allowed: true,
        channelId: channel.id,
        principalId: user.principalId,
        matchedRule: 'explicit_allow',
        evaluatedAt: now,
      };
    }

    // Step 3: Role Match
    if (user.role && policy.allowedRoles && policy.allowedRoles.includes(user.role)) {
      return {
        allowed: true,
        channelId: channel.id,
        principalId: user.principalId,
        matchedRule: 'role_match',
        evaluatedAt: now,
      };
    }

    // Step 4: Group Match
    if (user.userGroupIds && user.userGroupIds.length > 0 && policy.allowedUserGroupIds && policy.allowedUserGroupIds.length > 0) {
      const hasMatchingGroup = user.userGroupIds.some((gid) => policy.allowedUserGroupIds.includes(gid));
      if (hasMatchingGroup) {
        return {
          allowed: true,
          channelId: channel.id,
          principalId: user.principalId,
          matchedRule: 'group_match',
          evaluatedAt: now,
        };
      }
    }

    // Step 5: Public Access
    if (policy.publicAccess === 'anonymous' || (policy.publicAccess === 'authenticated' && user.isAuthenticated)) {
      return {
        allowed: true,
        channelId: channel.id,
        principalId: user.principalId,
        matchedRule: 'public_channel',
        evaluatedAt: now,
      };
    }

    // Step 6: Default Deny
    return {
      allowed: false,
      channelId: channel.id,
      principalId: user.principalId,
      matchedRule: 'default_deny',
      reason: 'No matching allow policy or group authorization',
      evaluatedAt: now,
    };
  }

  /**
   * Enforces channel access and emits security audit events on failure.
   */
  async enforceChannelAccess(
    channel: AgentChannelDefinition,
    user: UserAccessContext
  ): Promise<ChannelAccessVerdict> {
    const verdict = this.evaluateChannelAccess(channel, user);

    if (!verdict.allowed) {
      if (this.store) {
        await this.store.appendEvent({
          eventId: `sec-${Date.now()}`,
          kind: 'security.channel_access_denied' as any,
          actorId: user.principalId,
          originId: 'master-gateway',
          sequence: 1,
          occurredAt: new Date().toISOString(),
          payload: {
            channelId: channel.id,
            attemptedRole: user.role,
            userGroupIds: user.userGroupIds,
            matchedRule: verdict.matchedRule,
            denialReason: verdict.reason,
          },
        }).catch(() => {});
      }
      throw new ChannelAccessDeniedError(verdict);
    }

    return verdict;
  }

  /**
   * Evaluates granular tool/action permission inside an authorized channel.
   */
  evaluateToolPermission(
    channel: AgentChannelDefinition,
    toolName: string,
    user: UserAccessContext
  ): boolean {
    // If no action-level permissions defined on channel, inherit channel access
    if (!channel.actionPermissions || channel.actionPermissions.length === 0) {
      return true;
    }

    const perm = channel.actionPermissions.find((p) => p.toolName === toolName);
    if (!perm) {
      return true; // tool without custom policy inherits channel permission
    }

    // Explicit deny on tool
    if (perm.deniedPrincipalIds && perm.deniedPrincipalIds.includes(user.principalId)) {
      return false;
    }

    // Explicit allow on tool
    if (perm.allowedPrincipalIds && perm.allowedPrincipalIds.includes(user.principalId)) {
      return true;
    }

    // Role match
    if (user.role && perm.allowedRoles && perm.allowedRoles.includes(user.role)) {
      return true;
    }

    // Group match
    if (user.userGroupIds && perm.allowedGroupIds) {
      const match = user.userGroupIds.some((g) => perm.allowedGroupIds.includes(g));
      if (match) return true;
    }

    return false;
  }
}
